import { ProductDataProcessor, ProductRenderer, buildColorCodeToLabelMap } from '@theme/searchspring-core';
import { CONSTANTS as SEARCHSPRING_CONSTANTS } from '@theme/searchspring-core';
import { Component } from '@theme/component';
import { escapeHtml } from '@theme/text-utils';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { getArrowIcon } from '@theme/icons';
import { announceRender, trackingParams } from '@theme/searchspring-beacon';
import * as filterLogic from '@theme/searchspring-filter-logic';

export const CONSTANTS = {
  LOCAL_STORAGE_KEYS: {
    GRID_LAYOUT: 'four-col',
  },
  PRODUCT_TYPES: SEARCHSPRING_CONSTANTS.PRODUCT_TYPES,
  FILTER_FIELDS: SEARCHSPRING_CONSTANTS.FILTER_FIELDS,
};

/**
 * Where a facet list comes from (see fetchListSources): the finished facet
 * from the list-facet cache, or the filters, keys and response to work it
 * out from. `data.incomplete` (set by a subclass's fetchListSourceResponse)
 * marks a response missing a later page: #listFacet still reads it for this
 * draw, but never caches the facet it produces, so a later load fetches the
 * full response again rather than caching an incomplete result. `facet` is
 * `null` when the list source has no such facet (also cached, see
 * #listFacetCache), or `undefined` when it hasn't been computed yet.
 * @typedef {{ filters: Array<{field: string, values: string[]}>, listKey: string, prepareKey?: string, data?: any, facet?: any | null }} ListSource
 */

/**
 * Base class for Searchspring results components.
 * Provides common functionality for search and collection pages.
 * @abstract
 */
export class SearchspringResultsBase extends Component {
  /**
   * A list request slower than this is aborted and the facet keeps its
   * narrowed list. Enforced by #requestListSource racing the response
   * against the same deadline itself, not just by passing an AbortSignal to
   * fetch: a fetch wrapper (e.g. this theme's xhook) can replace that signal
   * and retry underneath it, so the network call may never actually honor
   * it.
   */
  static LIST_SOURCE_TIMEOUT_MS = 5000;

  /** @type {Array<{field: string, values: string[]}>} */
  appliedFilters = [];

  pagination = {
    totalResults: 0,
    begin: 0,
    end: 0,
    currentPage: 1,
    totalPages: 1,
    previousPage: 1,
    nextPage: 1,
    perPage: 24,
    defaultPerPage: 24,
  };

  /** @type {{ field: string | null, direction: string | null }} */
  sorting = {
    field: null,
    direction: null,
  };

  /** @type {Map<string, any>} */
  requestCache = new Map();

  /** Bumped by startFetch; see its doc. @type {number} */
  #fetchGeneration = 0;

  /**
   * Starts a fetch and returns its id. Filter clicks can overlap, so each
   * fetch checks isCurrentFetch before touching the page: a response that
   * arrives after a newer change started its own fetch is dropped.
   * @returns {number}
   */
  startFetch() {
    this.#fetchGeneration += 1;
    return this.#fetchGeneration;
  }

  /**
   * @param {number} fetchId - from startFetch
   * @returns {boolean} whether no newer fetch has started since
   */
  isCurrentFetch(fetchId) {
    return fetchId === this.#fetchGeneration;
  }

  /**
   * Updates the filter panel for the page `data` answers without holding up
   * its products. The page's own lists are prepared right away, so the
   * restricted-collection cache is written before the response is cached.
   * The panel is drawn at once: each picked list holds what it last showed
   * (see #shownListSources) until its list source arrives, so no facet shows
   * the previous state's options and no facet jumps. Neither draw happens
   * over a newer fetch or over picks made in the mobile drawer since the
   * fetch started (see #panelMatches).
   * @param {any} data
   * @param {Promise<Map<string, ListSource>>} listSources - from fetchListSources
   * @param {string} cacheKey - the cache key of the request `data` answers
   * @param {number} fetchId - from startFetch
   */
  applyFilterData(data, listSources, cacheKey, fetchId) {
    this.prepareFilterData(data, cacheKey);
    const facetsOnly = { facets: data.facets };
    // Draw the panel now, so no facet shows the previous state's options;
    // each picked list holds what it last showed until its source arrives.
    const shown = this.#shownListSources();
    if (this.#panelMatches(fetchId, cacheKey)) {
      this.setFilterData(facetsOnly, shown, cacheKey);
    }
    listSources
      .then((sources) => {
        // Redraw even when sources is empty as long as a placeholder was
        // shown: a field whose list source failed or timed out (see
        // LIST_SOURCE_TIMEOUT_MS, fetchListSourceData) must still fall back
        // to the page's own narrowed facet, not keep the placeholder forever.
        if ((sources.size > 0 || shown.size > 0) && this.#panelMatches(fetchId, cacheKey)) {
          this.setFilterData(facetsOnly, sources, cacheKey);
        }
      })
      .catch((error) => console.error('Searchspring filter update failed:', error));
  }

  /**
   * The Color / Color Group lists the panel last drew, by field. While a new
   * list source loads, a picked list keeps showing these instead of
   * shrinking to the matching styles' values. Updated in setFilterData to
   * whatever was actually drawn for each field (or deleted when nothing
   * was), so a cleared pick can't bring an old list back as a placeholder.
   * @type {Map<string, any>}
   */
  #shownListFacets = new Map();

  /**
   * Whether the panel may be redrawn for the fetch `fetchId` answers: no
   * newer fetch has started, and the filters are still the ones it fetched
   * for (on mobile, picks change the filters without fetching until Apply,
   * and a redraw from the older response would untick them).
   * @param {number} fetchId
   * @param {string} cacheKey
   * @returns {boolean}
   */
  #panelMatches(fetchId, cacheKey) {
    return this.isCurrentFetch(fetchId) && this.cacheKeyFor(this.appliedFilters) === cacheKey;
  }

  /**
   * Placeholder list sources for the immediate draw: for each list-source
   * facet with picks, the list the panel last showed, if any.
   * @returns {Map<string, ListSource>}
   */
  #shownListSources() {
    /** @type {Map<string, ListSource>} */
    const sources = new Map();
    for (const field of filterLogic.LIST_SOURCE_FIELDS) {
      const facet = this.#shownListFacets.get(field);
      if (facet && this.appliedFilters.some((filter) => filter.field === field)) {
        sources.set(field, { filters: [], listKey: '', facet });
      }
    }
    return sources;
  }

  /**
   * In-flight list-source requests, keyed by listSourceCacheKey, or
   * `${requestKey}#list-source` when listsDependOnPaging() (so a sort or
   * page-size change with a color picked reuses the same entry, except for a
   * paging-dependent subclass, which needs a fresh one). Shared by concurrent
   * loads of the same key; each entry is removed as soon as its request
   * settles (success or failure), so this only ever holds requests still in
   * flight — a settled facet is reused via #listFacetCache instead.
   * @type {Map<string, Promise<any | null>>}
   */
  #listSourceCache = new Map();

  /**
   * Finished list facets by `${field} ${listKey}`. A list facet depends only
   * on its list source, so once worked out it's reused as is — no refetch,
   * no recomputing restricted lists — and the raw response is released. Also
   * holds `null` for "this list source has no such facet" (e.g. a zero-result
   * state), so that's cached too, rather than refetched on every change; only
   * ever set from a complete response (see #listFacet).
   * @type {Map<string, any | null>}
   */
  #listFacetCache = new Map();

  /** @type {any[] | null} */
  sortingOptions = null;

  /** @type {boolean} */
  isMobile = isMobileBreakpoint();

  /** @type {string} */
  siteId = '';

  /** @type {any[] | null} */
  banners = null;

  /**
   * Correlates every beacon event with the response that produced these
   * results. Set on each fetch, including cache hits.
   * @type {string | null}
   */
  responseId = null;

  /** @type {Record<string, string[]>} */
  colorGroupsMap = {};

  /** @type {Map<string, string>} */
  colorCodeToLabelMap = new Map();

  /**
   * The userId, sessionId, pageLoadId and domain params Searchspring requires
   * for reporting and personalization, matching the beacon's own IDs.
   * @returns {string} A query string fragment starting with `&`, or empty.
   */
  getTrackingParams() {
    const params = new URLSearchParams(trackingParams()).toString();
    return params ? `&${params}` : '';
  }

  /**
   * Returns the URL with Searchspring tracking params (userId, sessionId,
   * pageLoadId, domain) removed, used as the cache key for prefetched
   * responses on `window.__ssPrefetch`.
   * @param {string} url
   * @returns {string}
   */
  static stripTrackingParams(url) {
    try {
      const u = new URL(url);
      u.searchParams.delete('userId');
      u.searchParams.delete('sessionId');
      u.searchParams.delete('pageLoadId');
      u.searchParams.delete('domain');
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /**
   * Builds the filter query string from applied filters (see
   * buildFilterQuery in searchspring-filter-logic.js).
   * @param {Array<{field: string, values: string[]}>} appliedFilters
   * @returns {string}
   */
  static buildFilterQuery(appliedFilters) {
    return filterLogic.buildFilterQuery(appliedFilters);
  }

  /**
   * Builds the pagination query string.
   * @param {number} currentPage
   * @returns {string}
   */
  static buildPaginationQuery(currentPage) {
    return currentPage === 1 ? '' : `&page=${currentPage}`;
  }

  /**
   * Builds the sort query string.
   * @param {{ field: string | null, direction: string | null }} sorting
   * @returns {string}
   */
  static buildSortQuery(sorting) {
    return filterLogic.buildSortQuery(sorting);
  }

  /**
   * The request URL for the applied filters on the current page.
   * @param {string} [trackingQuery] - The tracking params to send; by default
   *   the ones analytics consent allows right now. Pass '' for none.
   * @returns {string}
   */
  buildApiUrl(trackingQuery = this.getTrackingParams()) {
    return this.buildApiUrlFor(this.appliedFilters, trackingQuery);
  }

  /**
   * The request URL for `appliedFilters` on `page`. Must be implemented by subclasses.
   * @abstract
   * @param {Array<{field: string, values: string[]}>} appliedFilters
   * @param {string} trackingQuery
   * @param {number} [page]
   * @returns {string}
   */
  buildApiUrlFor(appliedFilters, trackingQuery, page = this.pagination.currentPage) {
    throw new Error('buildApiUrlFor must be implemented by subclass');
  }

  /**
   * The request cache key for `appliedFilters` on `page`: the URL without
   * tracking params, which change once consent arrives. The prefetch snippet
   * stores its response under the same key.
   * @param {Array<{field: string, values: string[]}>} appliedFilters
   * @param {number} [page]
   * @returns {string}
   */
  cacheKeyFor(appliedFilters, page = this.pagination.currentPage) {
    return SearchspringResultsBase.stripTrackingParams(this.buildApiUrlFor(appliedFilters, '', page));
  }

  /**
   * Get the results count label. Must be implemented by subclasses.
   * @abstract
   * @returns {string}
   */
  getResultsCountLabel() {
    throw new Error('getResultsCountLabel must be implemented by subclass');
  }

  /**
   * Check if the component has required data to fetch. Must be implemented by subclasses.
   * @abstract
   * @returns {boolean}
   */
  hasRequiredData() {
    throw new Error('hasRequiredData must be implemented by subclass');
  }

  async connectedCallback() {
    super.connectedCallback();

    // Require siteId to be provided via data-site-id attribute on the element
    this.siteId = this.dataset?.siteId || '';
    if (!this.siteId) {
      console.warn('SearchspringResultsBase: missing data-site-id attribute');
      return;
    }

    this.#loadColorGroups();
    this.#loadColorLabels();

    this.#getInitialFiltersFromUrl();
    this.#getPaginationFromUrl();
    this.#getSortingFromUrl();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    this.#loadGridLayout();
    this.#setupPerPageSelect();
    this.#setupModalListeners();

    if (!this.hasRequiredData()) {
      console.warn('SearchspringResultsBase: Missing required data.');
      return;
    }

    this.showSkeletonLoading();

    const productsWithOptions = await this.fetchProducts();
    if (productsWithOptions === null) return; // superseded by a newer fetch
    if (productsWithOptions.length === 0) {
      this.#renderEmptyState();
      return;
    }
    await this.renderProduct(productsWithOptions);
  }

  #setupPerPageSelect() {
    const select = this.querySelector('.searchspring-results__per-page');
    if (!select) return;
    select.value = String(this.pagination.perPage);
    select.addEventListener('change', (e) => {
      this.pagination.perPage = parseInt(e.target.value, 10);
      this.pagination.currentPage = 1;
      this.updatePaginationUrl();
      this.#updateUrlWithFilters();
      this.#updateUrlWithSorting();
      this.requestCache.clear();
      this.onFilterChanged();
    });
  }

  #setupModalListeners() {
    const expandButton = this.querySelector('.searchspring-results__expand-filters-button');
    const mobileFilters = this.querySelector('.searchspring-results__filters--mobile');
    const closeButton = this.querySelector('.modal-close');
    const overlay = this.querySelector('.modal-overlay');

    if (expandButton && mobileFilters) {
      expandButton.addEventListener('click', () => {
        mobileFilters.classList.toggle('active');
        this.#toggleScrollLock(mobileFilters.classList.contains('active'));
      });
    }

    if (closeButton && mobileFilters) {
      closeButton.addEventListener('click', () => {
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }

    if (overlay && mobileFilters) {
      overlay.addEventListener('click', () => {
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }

    const applyButton = this.querySelector('.modal-footer .apply');
    const clearAllButton = this.querySelector('.modal-footer .clear-all');

    if (applyButton && mobileFilters) {
      applyButton.addEventListener('click', () => {
        this.updatePaginationUrl();
        this.#updateUrlWithFilters();
        this.#updateUrlWithSorting();
        this.onFilterChanged();
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }

    if (clearAllButton && mobileFilters) {
      clearAllButton.addEventListener('click', () => {
        this.onClearAllFilters();
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }
  }

  /**
   * Toggles scroll-lock attribute on the HTML element.
   * @param {boolean} isLocked
   */
  #toggleScrollLock(isLocked) {
    if (isLocked) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  #renderEmptyState() {
    const container = this.querySelector('.searchspring-product-grid-container');
    const noResultsMessage = this.querySelector('.search-results__no-results');

    if (container) {
      container.innerHTML = `
        <div class="searchspring-empty-state">
          <h2>No products found.</h2>
        </div>
      `;
    }

    if (noResultsMessage instanceof HTMLElement) {
      noResultsMessage.classList.remove('hidden');
    }

    this.classList.add('products-empty');

    this.renderPagination();
  }

  /**
   * Process API response data. Can be overridden by subclasses.
   * @param {any[]} products
   * @returns {any[]}
   */
  processProducts(products) {
    const productProcessor = new ProductDataProcessor();
    const formattedProducts = productProcessor.formatProducts(products);
    // Attach per-type prices so product cards can filter swatches by availability
    for (const product of formattedProducts) {
      product.mtoPrices = product.pricesByType?.[CONSTANTS.PRODUCT_TYPES.MTO] || {};
      product.rtsPrices = product.pricesByType?.[CONSTANTS.PRODUCT_TYPES.RTS] || {};
    }
    return productProcessor.mapProductOptions(formattedProducts);
  }

  /**
   * Fetches the products for the current page from the Searchspring API.
   *
   * Searchspring reserves the banner cells server-side: the first page returns
   * `perPage - bannerCount` products (the banners fill the remaining grid cells
   * at their configured positions) and later pages return full pages, so its
   * pagination is already contiguous with no products dropped. We render the
   * API's products as-is and only gate the banners to the first page
   * (see renderProduct).
   * @returns {Promise<any[] | null>} null when a newer fetch superseded this
   *   one — callers then do nothing.
   */
  async fetchProducts() {
    const fetchId = this.startFetch();
    const url = this.buildApiUrl();
    const key = this.cacheKeyFor(this.appliedFilters);
    // Started first so the list sources load alongside the page itself.
    const listSources = this.fetchListSources();

    if (this.requestCache.has(key)) {
      const cached = this.requestCache.get(key);
      const userPerPage = this.pagination.perPage;
      this.pagination = { ...cached.pagination, perPage: userPerPage };
      this.applyFilterData({ facets: cached.facets }, listSources, key, fetchId);
      this.updateResultsCount();
      this.banners = cached.banners && cached.banners.length ? cached.banners : null;
      // A cached response was genuinely served by Searchspring, so replay its
      // responseId. Minting a new one would reference a response they never sent.
      this.responseId = cached.responseId || null;
      return cached.products;
    }

    try {
      const prefetched = typeof window !== 'undefined' && window.__ssPrefetch
        ? window.__ssPrefetch[key]
        : null;

      let data;
      let fromPrefetch = false;
      if (prefetched) {
        delete window.__ssPrefetch?.[key];
        data = await prefetched;
        fromPrefetch = Boolean(data);
      }
      if (!data) {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        data = await response.json();
      }

      // The prefetch can resolve before sibling module scripts
      // (searchspring-search-filters.js, searchspring-product-card.js) have
      // evaluated and registered their custom elements. Wait so
      // setFilterData/renderProducts find upgraded elements rather than
      // no-op'ing on plain HTMLElement.
      if (fromPrefetch) {
        await Promise.all([
          customElements.whenDefined('searchspring-search-filters'),
          customElements.whenDefined('searchspring-product-card'),
        ]);
      }

      // A newer filter change started its own fetch while this one waited.
      if (!this.isCurrentFetch(fetchId)) return null;

      const sorting = data?.sorting?.options || [];
      if (sorting.length > 0) {
        this.sortingOptions = sorting;
        this.updateSortingOptions(sorting);
      }

      const userPerPage = this.pagination.perPage;
      this.pagination = { ...data.pagination, perPage: userPerPage };
      this.applyFilterData(data, listSources, key, fetchId);
      this.updateResultsCount();

      const productsWithOptions = this.processProducts(data.results || []);

      // Banners are echoed on every page's response; renderProduct only draws
      // them on the first page.
      const banners = this.#extractBanners(data);
      this.banners = banners.length > 0 ? banners : null;

      this.responseId = data.responseId || null;

      this.requestCache.set(key, {
        products: productsWithOptions,
        pagination: data.pagination,
        facets: data.facets,
        banners,
        responseId: this.responseId,
      });

      return productsWithOptions;
    } catch (error) {
      if (!this.isCurrentFetch(fetchId)) return null;
      console.error('Searchspring API error:', error);
      return [];
    }
  }

  /**
   * Whether list sources depend on sort and page size: true when a subclass
   * builds lists from one page of products rather than from facets or from
   * every page. Such list keys keep those params.
   * @returns {boolean}
   */
  listsDependOnPaging() {
    return false;
  }

  /**
   * Fetches a list source for each list-source facet with picks
   * (LIST_SOURCE_FIELDS): the same request, on page 1, without that facet's
   * own picks. Searchspring works out every facet after the background
   * filters, so without this a Color pick would shrink the Color list to the
   * colors of the matching styles. The lists don't depend on the page, so
   * paging with a pick costs no request — except when listsDependOnPaging(),
   * where the list key keeps sort and page size so a change reuses nothing.
   * @returns {Promise<Map<string, ListSource>>}
   */
  async fetchListSources() {
    /** @type {Map<string, ListSource>} */
    const sources = new Map();
    const fields = filterLogic.LIST_SOURCE_FIELDS.filter((field) =>
      this.appliedFilters.some((filter) => filter.field === field)
    );
    await Promise.all(
      fields.map(async (field) => {
        const filters = filterLogic.withoutPicks(this.appliedFilters, field);
        const requestKey = this.cacheKeyFor(filters, 1);
        // The suffix keeps a paging-dependent list key from ever equalling a
        // main response's key, since forgetPreparedData deletes by list key.
        // (listSourceCacheKey's own output can't equal a main key either:
        // main keys always carry resultsPerPage.)
        const listKey = this.listsDependOnPaging()
          ? `${requestKey}#list-source`
          : filterLogic.listSourceCacheKey(requestKey);
        const cacheId = `${field} ${listKey}`;
        if (this.#listFacetCache.has(cacheId)) {
          sources.set(field, { filters, listKey, facet: this.#listFacetCache.get(cacheId) });
          return;
        }
        const source = await this.fetchListSourceData(filters, requestKey, listKey);
        if (source) sources.set(field, { filters, listKey, ...source });
      })
    );
    return sources;
  }

  /**
   * One list source's raw material: `{ data, prepareKey }` from the request
   * cache when the shopper has just seen that state (coherent because the
   * page's own applyFilterData prepares before the response is cached);
   * otherwise the raw response, fetched once and shared with any concurrent
   * load of the same list key. Sent without tracking IDs or `beacon=true`,
   * so Searchspring doesn't report it as a second search, and asking for one
   * result unless listSourceNeedsResults() says the page needs products.
   * Aborted after LIST_SOURCE_TIMEOUT_MS. Null when the request fails or
   * times out; the facet then keeps its narrowed list.
   * @param {Array<{field: string, values: string[]}>} filters
   * @param {string} requestKey - cacheKeyFor(filters, 1)
   * @param {string} listKey - listSourceCacheKey(requestKey), or
   *   `${requestKey}#list-source` when listsDependOnPaging()
   * @returns {Promise<{ data: any, prepareKey: string } | null>}
   */
  async fetchListSourceData(filters, requestKey, listKey) {
    // A main response the shopper saw: its restricted lists are cached under
    // its own key (applyFilterData prepares before the response is cached).
    const cached = this.requestCache.get(requestKey);
    if (cached) return { data: { facets: cached.facets }, prepareKey: requestKey };

    if (!this.#listSourceCache.has(listKey)) {
      this.#listSourceCache.set(listKey, this.#requestListSource(filters, requestKey, listKey));
    }
    const data = await this.#listSourceCache.get(listKey);
    return data ? { data, prepareKey: listKey } : null;
  }

  /**
   * Fetches one list source's raw response, sharing #listSourceCache's
   * in-flight entry with any concurrent load. Aborted after
   * LIST_SOURCE_TIMEOUT_MS. The cache entry is removed once the request
   * settles either way — a successful response is retained only as long as
   * #listFacet needs it, and a failed one lets a later load retry.
   * @param {Array<{field: string, values: string[]}>} filters
   * @param {string} requestKey - cacheKeyFor(filters, 1)
   * @param {string} listKey - listSourceCacheKey(requestKey), or
   *   `${requestKey}#list-source` when listsDependOnPaging()
   * @returns {Promise<any | null>}
   */
  async #requestListSource(filters, requestKey, listKey) {
    try {
      const url = new URL(requestKey);
      url.searchParams.delete('beacon');
      if (!this.listSourceNeedsResults()) url.searchParams.set('resultsPerPage', '1');
      const signal = SearchspringResultsBase.#timeoutSignal(SearchspringResultsBase.LIST_SOURCE_TIMEOUT_MS);
      // `signal` is still passed to fetch so the request is really cancelled
      // wherever it IS honored, but a fetch wrapper (e.g. this theme's xhook,
      // frontend/js/alpine.js) can replace the caller's signal with its own
      // and retry underneath it, so the network call may never actually see
      // an abort. Race the whole response ourselves so LIST_SOURCE_TIMEOUT_MS
      // holds regardless. A response that arrives after losing the race is
      // simply dropped — fetchListSourceResponse has no side effects, so
      // nothing else needs to happen with it.
      return await Promise.race([
        this.fetchListSourceResponse(url, signal),
        SearchspringResultsBase.#rejectOnAbort(signal),
      ]);
    } catch (error) {
      console.warn('Searchspring list source failed; the filter lists only matching values.', filters, error);
      return null;
    } finally {
      this.#listSourceCache.delete(listKey);
    }
  }

  /**
   * An AbortSignal that fires after `ms`. Uses AbortSignal.timeout where
   * available; falls back to an AbortController + setTimeout for Safari/iOS
   * < 16, which don't implement AbortSignal.timeout.
   * @param {number} ms
   * @returns {AbortSignal}
   */
  static #timeoutSignal(ms) {
    if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms);
    const controller = new AbortController();
    setTimeout(() => controller.abort(new DOMException('The operation timed out.', 'TimeoutError')), ms);
    return controller.signal;
  }

  /**
   * A promise that rejects once `signal` aborts — immediately if it already
   * has, from the `abort` event otherwise — with `signal.reason`, or a
   * `TimeoutError` DOMException when a signal aborts without one. Raced
   * against fetchListSourceResponse in #requestListSource so
   * LIST_SOURCE_TIMEOUT_MS is enforced even when a fetch wrapper drops the
   * signal before it reaches the network (see LIST_SOURCE_TIMEOUT_MS's doc).
   * @param {AbortSignal} signal
   * @returns {Promise<never>}
   */
  static #rejectOnAbort(signal) {
    return new Promise((_, reject) => {
      const onAbort = () => reject(signal.reason ?? new DOMException('The operation timed out.', 'TimeoutError'));
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  /**
   * Fetches a list source's response. Subclasses that need more than the
   * first page override this.
   * @param {URL} url
   * @param {AbortSignal} [signal]
   * @returns {Promise<any>}
   */
  async fetchListSourceResponse(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`API request failed: ${response.status}`);
    return response.json();
  }

  /**
   * Whether list sources must carry products rather than just facets: true
   * when a subclass works out its lists from products (see
   * prepareFilterData) instead of reading `data.facets` directly. The base
   * class only reads facets, so it returns false.
   * @returns {boolean}
   */
  listSourceNeedsResults() {
    return false;
  }

  /**
   * Handles changes to filters.
   * @param {string} field
   * @param {string} value
   * @param {boolean} checked
   */
  onChangeFilter(field, value, checked) {
    const currentFilter = this.appliedFilters.find((filter) => filter.field === field);

    if (!currentFilter) {
      if (checked) {
        this.appliedFilters.push({ field, values: [value] });
      }
    } else {
      if (checked) {
        if (!currentFilter.values.includes(value)) {
          currentFilter.values.push(value);
        }
      } else {
        currentFilter.values = currentFilter.values.filter((v) => v !== value);
        if (currentFilter.values.length === 0) {
          this.appliedFilters = this.appliedFilters.filter((f) => f.field !== field);
        }
      }
    }

    if (!this.isMobile) {
      this.pagination.currentPage = 1;
      this.updatePaginationUrl();
      this.#updateUrlWithFilters();
      this.onFilterChanged();
    } else {
      this.pagination.currentPage = 1;
    }
  }

  /**
   * Clears all filters for a specific field.
   * @param {string} field
   */
  onClearAllFilter(field) {
    this.appliedFilters = this.appliedFilters.filter((f) => f.field !== field);
    this.pagination.currentPage = 1;
    this.updatePaginationUrl();
    this.#updateUrlWithFilters();
    this.onFilterChanged();
  }

  /**
   * Clears all filters.
   */
  onClearAllFilters() {
    this.appliedFilters = [];
    this.pagination.currentPage = 1;
    this.updatePaginationUrl();
    this.#updateUrlWithFilters();
    this.onFilterChanged();
  }

  #getInitialFiltersFromUrl() {
    const url = new URL(window.location.href);
    const filterParams = url.searchParams.getAll('filter');

    this.appliedFilters = [];

    filterParams.forEach((filterParam) => {
      const colonIndex = filterParam.indexOf(':');
      if (colonIndex === -1) return;

      const field = filterParam.substring(0, colonIndex);
      const value = filterParam.substring(colonIndex + 1);

      if (field && value) {
        const existingFilter = this.appliedFilters.find((filter) => filter.field === field);

        if (existingFilter) {
          if (!existingFilter.values.includes(value)) {
            existingFilter.values.push(value);
          }
        } else {
          this.appliedFilters.push({
            field: field,
            values: [value],
          });
        }
      }
    });
  }

  /**
   * Default "Best Match" sort option. Empty field/direction means no sort is
   * sent to the API and no client-side sort is applied — merchandising order
   * is preserved.
   */
  static BEST_MATCH_OPTION = { field: '', direction: '', label: 'Best Match' };

  /**
   * Updates sorting options on sort components. Prepends a "Best Match"
   * option that, when selected, clears sorting.
   * @param {any[]} sorting
   */
  updateSortingOptions(sorting) {
    const sortComponents = this.querySelectorAll('searchspring-sort-by');
    const optionsWithDefault = [SearchspringResultsBase.BEST_MATCH_OPTION, ...sorting];

    if (!this.sorting.direction || !this.sorting.field) {
      this.sorting = { ...SearchspringResultsBase.BEST_MATCH_OPTION };
    }

    sortComponents.forEach((sortComponent) => {
      if (
        sortComponent instanceof HTMLElement &&
        typeof /** @type {any} */ (sortComponent).updateSearchingOptions === 'function'
      ) {
        /** @type {any} */ (sortComponent).updateSearchingOptions(optionsWithDefault, this.sorting);
      }
    });
  }

  #updateUrlWithFilters() {
    const url = new URL(window.location.href);
    url.searchParams.delete('filter');

    this.appliedFilters.forEach((filter) => {
      filter.values.forEach((value) => {
        url.searchParams.append('filter', `${filter.field}:${value}`);
      });
    });

    window.history.replaceState({}, '', url);
  }

  #getPaginationFromUrl() {
    const url = new URL(window.location.href);
    const pageParam = url.searchParams.get('page');
    const pageNumber = pageParam ? parseInt(pageParam, 10) : 1;

    if (!isNaN(pageNumber) && pageNumber > 0) {
      this.pagination.currentPage = pageNumber;
    } else {
      this.pagination.currentPage = 1;
    }

    const perPageParam = url.searchParams.get('perPage');
    if (perPageParam) {
      const perPage = parseInt(perPageParam, 10);
      if (!isNaN(perPage) && perPage > 0) {
        this.pagination.perPage = perPage;
      }
    }
  }

  #getSortingFromUrl() {
    const url = new URL(window.location.href);
    const sortParams = url.searchParams;

    for (const [key, value] of sortParams) {
      if (key.startsWith('sort.')) {
        const field = key.substring(5);
        this.sorting = { field, direction: value };
        this.onSortingLoadedFromUrl();
        break;
      }
    }
  }

  /**
   * Called when sorting is loaded from URL. Can be overridden by subclasses.
   */
  onSortingLoadedFromUrl() {
    // Override in subclass if needed
  }

  /**
   * Called when user changes sorting. Can be overridden by subclasses.
   */
  onUserChangedSorting() {
    // Override in subclass if needed
  }

  updatePaginationUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('perPage');

    if (this.pagination.currentPage > 1) {
      url.searchParams.append('page', this.pagination.currentPage.toString());
    }

    if (this.pagination.perPage !== this.pagination.defaultPerPage) {
      url.searchParams.append('perPage', this.pagination.perPage.toString());
    }

    window.history.replaceState({}, '', url);
  }

  #updateUrlWithSorting() {
    const url = new URL(window.location.href);

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('sort.')) {
        url.searchParams.delete(key);
      }
    }

    if (this.sorting.field && this.sorting.direction) {
      url.searchParams.append(`sort.${this.sorting.field}`, this.sorting.direction);
    }

    window.history.replaceState({}, '', url);
  }

  /**
   * Handles sorting changes.
   * @param {{ field: string, direction: string }} activeOption
   */
  onChangeSorting(activeOption) {
    this.sorting.field = activeOption.field;
    this.sorting.direction = activeOption.direction;

    // Call onUserChangedSorting() if implemented by subclass
    this.onUserChangedSorting();

    if (this.sortingOptions) {
      this.updateSortingOptions(this.sortingOptions);
    }

    if (!this.isMobile) {
      this.pagination.currentPage = 1;
      this.updatePaginationUrl();
      this.#updateUrlWithSorting();
      this.onFilterChanged();
    } else {
      this.pagination.currentPage = 1;
    }
  }

  /**
   * Handles filter changes by re-fetching and re-rendering.
   */
  async onFilterChanged() {
    this.showSkeletonLoading();
    const productsWithOptions = await this.fetchProducts();
    if (productsWithOptions === null) return; // superseded by a newer fetch
    if (productsWithOptions.length === 0) {
      this.#renderEmptyState();
      return;
    }
    await this.renderProduct(productsWithOptions);
  }

  /**
   * Parse color groups mapping from data-color-groups attribute.
   */
  #loadColorGroups() {
    const attr = this.getAttribute('data-color-groups');
    if (!attr) return;
    try {
      const raw = JSON.parse(attr);
      for (const [key, codes] of Object.entries(raw)) {
        this.colorGroupsMap[key.toLowerCase()] = /** @type {string[]} */ (codes);
      }
    } catch (e) {
      console.warn('Failed to parse color groups:', e);
    }
  }

  /**
   * Parse color labels and build code→label map from data-color-labels attribute.
   */
  #loadColorLabels() {
    const attr = this.getAttribute('data-color-labels');
    if (!attr) return;
    try {
      const colorLabelsArr = JSON.parse(attr);
      this.colorCodeToLabelMap = buildColorCodeToLabelMap(colorLabelsArr);
    } catch (e) {
      // ignore parse errors
    }
  }

  /**
   * Gets active color filter values from applied filters,
   * @returns {string[]}
   */
  getActiveColorFilters() {
    const colorFilter = this.appliedFilters.find(
      (filter) => filter.field === CONSTANTS.FILTER_FIELDS.COLORS
    );
    const colorValues = colorFilter?.values || [];

    const colorGroupFilter = this.appliedFilters.find(
      (filter) => filter.field === CONSTANTS.FILTER_FIELDS.COLOR_GROUPS
    );
    if (!colorGroupFilter || colorGroupFilter.values.length === 0) {
      return colorValues;
    }

    const resolvedCodes = colorGroupFilter.values.flatMap(
      (groupName) => this.colorGroupsMap[groupName.toLowerCase()] || []
    );

    // Cards tell them apart by format: a direct pick is "CODE:#hex", a code
    // from a group is bare (see promotedSwatchCodes).
    return [...resolvedCodes, ...colorValues];
  }

  /**
   * Gets active availability filter value from applied filters.
   * @returns {string | null}
   */
  getActiveAvailabilityFilter() {
    const availabilityFilter = this.appliedFilters.find(
      (filter) => filter.field === 'mfield_searchspring_availability'
    );
    return availabilityFilter?.values?.[0] || null;
  }

  /**
   * Gets active size filter value from applied filters.
   * @returns {string | null}
   */
  getActiveSizeFilter() {
    const sizeFilter = this.appliedFilters.find(
      (filter) => filter.field === 'mfield_searchspring_sizes'
    );
    return sizeFilter?.values?.[0] || null;
  }

  /**
   * Extract banners from API response merchandising content.
   * @param {any} data - API response data
   * @returns {any[]} Array of banners with position information
   */
  #extractBanners(data) {
    /** @type {any[]} */
    const banners = [];

    if (!data?.merchandising?.content?.inline) {
      return banners;
    }

    const inlineContent = data.merchandising.content.inline;

    if (Array.isArray(inlineContent)) {
      inlineContent.forEach((item) => {
        if (item?.config?.position?.index !== undefined) {
          banners.push({
            ...item,
            positionIndex: item.config.position.index,
          });
        }
      });
    }

    // Sort banners by position index
    banners.sort((a, b) => a.positionIndex - b.positionIndex);

    return banners;
  }

  /**
   * Number of product grid cells a banner occupies. The span is encoded in the
   * banner image alt text as "<span>::<alt>" and mirrors the `--col-span` the
   * renderer applies. Defaults to 1.
   * @param {any} banner
   * @returns {number}
   */
  static getBannerColSpan(banner) {
    const value = banner?.value;
    if (typeof value !== 'string') return 1;
    const imgTagMatch = value.match(/<img[^>]+alt="([^">]+)"[^>]*>/i);
    if (imgTagMatch && imgTagMatch[1]) {
      const parts = imgTagMatch[1].split('::');
      if (parts.length === 2) {
        const span = Number.parseInt(parts[0].trim(), 10);
        if (!Number.isNaN(span) && span > 0) return span;
      }
    }
    return 1;
  }

  /**
   * Total product grid cells reserved by the first-page banners. Each banner
   * spans one or more columns (see getBannerColSpan). Capped below perPage so
   * it can never consume an entire page. Banners are the same on every page's
   * response, so this offset is stable across pagination.
   * @returns {number}
   */
  getBannerCells() {
    const banners = this.banners || [];
    if (banners.length === 0) return 0;
    const cells = banners.reduce(
      (sum, banner) => sum + SearchspringResultsBase.getBannerColSpan(banner),
      0
    );
    return Math.max(0, Math.min(cells, this.pagination.perPage - 1));
  }

  /**
   * The slice of the flat product list to show on a display page. The first
   * page gives up `bannerCells` product slots to the banners; every later page
   * is shifted back by the same amount so no products are skipped between pages.
   * @param {number} page
   * @param {number} [bannerCells]
   * @returns {{ start: number, count: number }}
   */
  getDisplayWindow(page, bannerCells = this.getBannerCells()) {
    const perPage = this.pagination.perPage;
    if (page <= 1) {
      return { start: 0, count: Math.max(0, perPage - bannerCells) };
    }
    return { start: (page - 1) * perPage - bannerCells, count: perPage };
  }

  /**
   * Total number of display pages once first-page banners have consumed
   * `bannerCells` product slots. Page 1 holds `perPage - bannerCells` products;
   * the remainder fill full pages, so the count can exceed the API's page count
   * by one.
   * @param {number} totalResults
   * @param {number} [bannerCells]
   * @returns {number}
   */
  getDisplayTotalPages(totalResults, bannerCells = this.getBannerCells()) {
    const perPage = this.pagination.perPage;
    const firstPageCount = Math.max(0, perPage - bannerCells);
    if (totalResults <= firstPageCount) return 1;
    return 1 + Math.ceil((totalResults - firstPageCount) / perPage);
  }

  /**
   * Renders the products in the grid container.
   * @param {any[]} products
   * @param {boolean} [append=false]
   */
  async renderProduct(products, append = false) {
    const container = this.querySelector('.searchspring-product-grid-container');
    const noResultsMessage = this.querySelector('.search-results__no-results');
    if (!container) return;

    const activeColorFilters = this.getActiveColorFilters();
    const activeAvailabilityFilter = this.getActiveAvailabilityFilter();
    const activeSizeFilter = this.getActiveSizeFilter();

    // Banners render only on the first page. Searchspring already reserves the
    // banner cells server-side (page 1 returns perPage - bannerCount products),
    // and virtual pagination reserves them client-side (see getDisplayWindow),
    // so the grid stays a full perPage-cell block.
    const banners = this.pagination.currentPage === 1 && !append ? this.banners || [] : [];

    const productRenderer = new ProductRenderer(/** @type {HTMLElement} */(container));
    await productRenderer.renderProducts(products, false, append, { activeColorFilters, activeAvailabilityFilter, activeSizeFilter, banners, colorCodeToLabelMap: this.colorCodeToLabelMap });

    // Cards and banners register themselves as impression targets when they
    // mount, so this only has to report that the set was displayed.
    announceRender(this);

    if (noResultsMessage instanceof HTMLElement) {
      noResultsMessage.classList.add('hidden');
    }

    this.classList.remove('products-empty');

    this.renderPagination();
  }

  updateResultsCount() {
    const resultsCountElement = this.querySelector('.results-count');
    if (resultsCountElement) {
      resultsCountElement.textContent = this.getResultsCountLabel();
    }
  }

  /**
   * Renders standard pagination controls (prev/page-numbers/next) into
   * the `.searchspring-pagination` container. Hides the container when
   * there is only a single page of results.
   */
  renderPagination() {
    const container = this.querySelector('.searchspring-pagination');
    if (!(container instanceof HTMLElement)) return;

    const { currentPage, totalPages } = this.pagination;

    if (!totalPages || totalPages <= 1) {
      container.setAttribute('hidden', '');
      container.innerHTML = '';
      return;
    }

    container.removeAttribute('hidden');

    const pages = SearchspringResultsBase.#buildPageList(currentPage, totalPages);
    const prevPage = Math.max(1, currentPage - 1);
    const nextPage = Math.min(totalPages, currentPage + 1);
    const prevDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= totalPages;

    const items = pages.map((p) => {
      if (p === '…') {
        return '<li class="searchspring-pagination__ellipsis" aria-hidden="true">…</li>';
      }
      const isActive = p === currentPage;
      return `
        <li>
          <button
            type="button"
            class="searchspring-pagination__page${isActive ? ' is-active' : ''}"
            data-page="${p}"
            ${isActive ? 'aria-current="page"' : ''}
            aria-label="Page ${p}"
          >${p}</button>
        </li>`;
    }).join('');

    container.innerHTML = `
      <button
        type="button"
        class="searchspring-pagination__arrow searchspring-pagination__arrow--prev"
        data-page="${prevPage}"
        ${prevDisabled ? 'disabled' : ''}
        aria-label="Previous page"
      >${getArrowIcon()}</button>
      <ul class="searchspring-pagination__list">${items}</ul>
      <button
        type="button"
        class="searchspring-pagination__arrow"
        data-page="${nextPage}"
        ${nextDisabled ? 'disabled' : ''}
        aria-label="Next page"
      >${getArrowIcon()}</button>
    `;

    container.querySelectorAll('button[data-page]').forEach((button) => {
      button.addEventListener('click', (event) => {
        const target = /** @type {HTMLButtonElement} */ (event.currentTarget);
        if (target.hasAttribute('disabled')) return;
        const page = parseInt(target.getAttribute('data-page') || '', 10);
        if (!isNaN(page)) this.goToPage(page);
      });
    });
  }

  /**
   * Build a condensed list of page numbers with ellipses around the current page.
   * Always includes first and last pages. Inserts '…' placeholders when there is
   * a gap of more than one page.
   * @param {number} current
   * @param {number} total
   * @returns {(number | '…')[]}
   */
  static #buildPageList(current, total) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    /** @type {(number | '…')[]} */
    const pages = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('…');

    pages.push(total);
    return pages;
  }

  /**
   * Navigate to a specific page. Replaces the current product grid
   * (does not append) and scrolls to the top of the grid.
   * @param {number} pageNumber
   */
  async goToPage(pageNumber) {
    if (!Number.isFinite(pageNumber)) return;
    if (pageNumber < 1 || pageNumber > this.pagination.totalPages) return;
    if (pageNumber === this.pagination.currentPage) return;

    this.pagination.currentPage = pageNumber;
    this.updatePaginationUrl();

    this.scrollToGridTop();
    this.showSkeletonLoading();

    const productsWithOptions = await this.fetchProducts();
    if (productsWithOptions === null) return; // superseded by a newer fetch
    if (productsWithOptions.length === 0) {
      this.#renderEmptyState();
      return;
    }

    await this.renderProduct(productsWithOptions);
  }

  scrollToGridTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Gets filter data from API response.
   * @param {any} data
   * @returns {any[]}
   */
  getFilterData(data) {
    return data.facets || [];
  }

  /**
   * Hook: works out from `data` whatever getFilterData needs. Runs right
   * before each getFilterData call, with the applied filters `data` answers.
   * @param {any} data
   * @param {string} cacheKey - identifies the request `data` answers
   */
  prepareFilterData(data, cacheKey) {}

  /**
   * Hook: drop whatever prepareFilterData cached for `cacheKey`. Called once
   * a list source's facet has been read, so a subclass that caches prepared
   * lists per key (e.g. a restricted collection's allowed-colors data, built
   * from the list source's raw products) can release them; the cache key is
   * never looked up again, since later loads read the finished facet
   * instead. Subclasses that cache prepared lists override it.
   * @param {string} cacheKey
   */
  forgetPreparedData(cacheKey) {}

  /**
   * Sets filter data on filter components. The Color and Color Group lists
   * come from their list sources (see fetchListSources) when there are any.
   * @param {any} data
   * @param {Map<string, ListSource>} listSources
   * @param {string} cacheKey - the cache key of the request `data` answers
   */
  setFilterData(data, listSources, cacheKey) {
    /** @type {Map<string, any>} */
    const listFacets = new Map();
    for (const [field, source] of listSources) {
      const facet = source.facet !== undefined ? source.facet : this.#listFacet(field, source);
      if (facet) listFacets.set(field, facet);
    }

    // Last, so the state getFilterData reads describes this page.
    this.prepareFilterData(data, cacheKey);
    const filterData = filterLogic.mergeListFacets(
      this.getFilterData(data),
      listFacets,
      this.appliedFilters
    );

    // Record what was actually drawn for each list-source field (a narrowed
    // fallback counts too), so a cleared pick can't bring an old list back
    // as a placeholder on a later pick of the same field. Prefer the list
    // source's own facet over the merged one mergeListFacets produced: the
    // merge can carry picks in from the page's facet or as bare entries
    // (see mergeListFacets), which a placeholder shouldn't repeat.
    for (const field of filterLogic.LIST_SOURCE_FIELDS) {
      const drawn = filterData.find((facet) => facet.field === field);
      const shownFacet = listFacets.get(field) ?? drawn;
      if (shownFacet) this.#shownListFacets.set(field, shownFacet);
      else this.#shownListFacets.delete(field);
    }

    const filterComponents = this.querySelectorAll('searchspring-search-filters');
    filterComponents.forEach((filterComponent) => {
      if (
        filterComponent instanceof HTMLElement &&
        typeof /** @type {any} */ (filterComponent).setData === 'function'
      ) {
        /** @type {any} */ (filterComponent).setData(filterData);
      }
    });
  }

  /**
   * `field`'s facet as its list source shows it. getFilterData and
   * prepareFilterData read the applied filters, so they're swapped for the
   * list source's; it's all synchronous, so nothing else runs meanwhile.
   * Once worked out, the facet is cached — unless `data.incomplete` (a
   * later page of this list source failed; never cache an incomplete
   * result, see ListSource) — and whatever prepareFilterData cached for a
   * raw list response is forgotten; later loads reuse the facet via
   * source.facet, so this only runs once per list (or every time, while
   * incomplete).
   * @param {string} field
   * @param {ListSource} source
   * @returns {any | null}
   */
  #listFacet(field, { filters, listKey, prepareKey, data }) {
    const applied = this.appliedFilters;
    this.appliedFilters = filters;
    let facet;
    try {
      this.prepareFilterData(data, prepareKey);
      facet = this.getFilterData(data).find((/** @type {any} */ facet) => facet.field === field) || null;
    } finally {
      this.appliedFilters = applied;
    }
    if (!data?.incomplete) {
      // The finished facet — or null, for "no such facet" — is all later
      // loads need.
      this.#listFacetCache.set(`${field} ${listKey}`, facet);
    }
    if (prepareKey === listKey) {
      // Prepared from a raw list response, not a cached main response —
      // release whatever prepareFilterData cached for it (e.g. a restricted
      // collection's allowed-colors data built from its products), whether
      // or not a facet for this field was found.
      this.forgetPreparedData(listKey);
    }
    return facet;
  }

  showSkeletonLoading(count = 8) {
    const container = this.querySelector('.searchspring-product-grid-container');
    const noResultsMessage = this.querySelector('.search-results__no-results');

    if (!container) return;

    const skeletonCard = `
      <div class="searchspring-skeleton-card">
        <div class="searchspring-skeleton-card__image skeleton-item"></div>
        <div class="searchspring-skeleton-card__title skeleton-item"></div>
        <div class="searchspring-skeleton-card__price skeleton-item"></div>
        <div class="searchspring-skeleton-card__swatches">
          <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
          <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
          <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
        </div>
      </div>
    `;

    const skeletonGrid = `
      <div class="searchspring-product-grid searchspring-skeleton-grid">
        ${skeletonCard.repeat(count)}
      </div>
    `;

    if (noResultsMessage instanceof HTMLElement) {
      noResultsMessage.classList.add('hidden');
    }

    container.innerHTML = skeletonGrid;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    const mobileFilters = this.querySelector('.searchspring-results__filters--mobile');
    if (mobileFilters?.classList.contains('active')) {
      this.#toggleScrollLock(false);
    }
  }

  /**
   * Updates the layout based on the input target.
   * @param {{ target: EventTarget | null }} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const gridColumnsCount = target.value;
    const grid = this.querySelector('.searchspring-product-grid-container');
    if (!grid) return;

    grid.setAttribute('data-columns', gridColumnsCount);
    this.#saveGridLayout(gridColumnsCount);
    this.#refreshCardPictures(grid);
  }

  /**
   * Re-renders picture elements on every product card in the grid so the browser
   * evaluates srcset with the updated column sizes. Simply updating the `sizes` DOM
   * attribute is insufficient — browsers won't re-request an already-loaded image.
   *
   * Deferred to the next animation frame so the CSS grid layout (driven by the
   * updated `data-columns` attribute) has fully resolved before we re-initialize
   * slideshow dimensions and pick the srcset candidate.
   * @param {Element} grid
   */
  #refreshCardPictures(grid) {
    requestAnimationFrame(() => {
      grid.querySelectorAll('searchspring-product-card').forEach((card) => {
        if (typeof /** @type {any} */ (card).refreshPictureSizes === 'function') {
          /** @type {any} */ (card).refreshPictureSizes();
        }
      });
    });
  }

  /**
   * @param {string} layout
   */
  #saveGridLayout(layout) {
    try {
      localStorage.setItem(CONSTANTS.LOCAL_STORAGE_KEYS.GRID_LAYOUT, layout);
    } catch (error) {
      console.warn('Failed to save grid layout to localStorage:', error);
    }
  }

  #loadGridLayout() {
    try {
      const savedLayout = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEYS.GRID_LAYOUT);
      const layout = savedLayout || 'four-col';

      const grid = this.querySelector('.searchspring-product-grid-container');
      if (grid) {
        grid.setAttribute('data-columns', layout);
      }

      const layoutInput = this.querySelector(`[value="${layout}"]`);
      if (layoutInput instanceof HTMLInputElement) {
        layoutInput.checked = true;
      }
    } catch (error) {
      console.warn('Failed to load grid layout from localStorage:', error);
    }
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.isMobile = !event.matches;

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    this.#setLayout(targetElement.value);
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const grid = this.querySelector('#searchspring-product-grid');
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#saveGridLayout(value);
  }
}

class SearchspringSortBy extends Component {
  sortingOptions = [];

  activeOption = {
    field: 'relevance',
    direction: 'desc',
    label: 'Best Match',
  };

  async connectedCallback() {
    super.connectedCallback();
    this.renderSortingOptions();
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
  }

  updateSearchingOptions(options, activeSorting) {
    this.sortingOptions = options;
    this.activeOption = activeSorting;
    this.activeOption.label =
      this.sortingOptions.find(
        (option) =>
          option.field === activeSorting.field && option.direction === activeSorting.direction
      )?.label || this.activeOption.label;
    this.renderSortingOptions();
  }

  toggleSortingOptions() {
    this.classList.toggle('active');
  }

  handleClickOutside = (event) => {
    if (!this.contains(event.target)) {
      this.classList.remove('active');
    }
  };

  renderSortingOptions() {
    if (!this.sortingOptions || this.sortingOptions.length === 0) return;
    const isMobile = this.getAttribute('data-type') === 'mobile';

    const template = `
            <div class="searchspring-sort-by">
              <div class="searchspring-sort-by__current" on:click="/toggleSortingOptions">
                <div class="searchspring-sort-by__label">Sort by</div>
                <span>${escapeHtml(this.activeOption.label)}</span>
                <span class="svg-wrapper icon-caret icon-animated">
                  <svg width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                    <path d="M4 7L9.35101 12.5118C9.98295 13.1627 11.017 13.1627 11.649 12.5118L17 7" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
                </div>
                <div class="searchspring-sort-by__options">
                    ${this.sortingOptions
                      .map((option) => {
                        const escapedField = escapeHtml(option.field);
                        const escapedDirection = escapeHtml(option.direction);
                        const escapedLabel = escapeHtml(option.label);
                        return `<div class="form-check">
                      <label for="sort-by-${escapedField}-${escapedDirection}">
                        <div class="flex items-center gap-1.5 cursor-pointer">
                          <input on:change="/onSortOptionChange" type="radio" name="sort-by" id="sort-by-${escapedField}-${escapedDirection}" data-label="${escapedLabel}" data-field="${escapedField}" value="${escapedDirection}" ${this.activeOption.field === option.field && this.activeOption.direction === option.direction ? 'checked' : ''} />
                          <span class="svg-wrapper icon-checkmark sorting-filter__checkmark">
                            <svg width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none" class="icon-checkmark">
                              <path d="M5.5 10L7.85101 12.5118C8.48295 13.1627 9.51704 13.1627 10.149 12.5118L15.5 7" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                          </span>
                          <span class="searchspring-sort-by__option-label">${escapedLabel}</span>
                        </div>
                      </label>
                    </div>`;
                      })
                      .join('')}
                </div>
            </div>
        `;

    const mobileTemplate = `
            <div class="searchspring-sort-by">
                <div class="form-field">
                <label for="sort-by" class="form-label">Sort By</label>
                <div class="form-select-wrapper">
                    <select on:change="/onSortOptionChange" id="sort-by" class="form-select">
                        ${this.sortingOptions
                          .map((option) => {
                            const escapedField = escapeHtml(option.field);
                            const escapedDirection = escapeHtml(option.direction);
                            const escapedLabel = escapeHtml(option.label);
                            return `<option data-label="${escapedLabel}" data-field="${escapedField}" value="${escapedDirection}" ${this.activeOption.field === option.field && this.activeOption.direction === option.direction ? 'selected' : ''}>${escapedLabel}</option>`;
                          })
                          .join('')}
                    </select>
                    <svg class="form-select__icon" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                      <path d="M4 7L9.35101 12.5118C9.98295 13.1627 11.017 13.1627 11.649 12.5118L17 7" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                </div>
                </div>
            </div>
        `;

    this.innerHTML = isMobile ? mobileTemplate : template;
  }

  onSortOptionClick(event) {
    const field = event.target.getAttribute('data-field');
    const direction = event.target.getAttribute('data-direction');
    const label = event.target.getAttribute('data-label');
    this.activeOption = { field, direction, label };
    this.onSortOptionChange(event);
  }

  onSortOptionChange(event) {
    const isMobile = this.getAttribute('data-type') === 'mobile';
    const selectedValue = event.target.value;
    const field = isMobile
      ? event.target.selectedOptions[0].getAttribute('data-field')
      : event.target.getAttribute('data-field');
    const direction = selectedValue;
    const label = isMobile
      ? event.target.selectedOptions[0].getAttribute('data-label')
      : event.target.getAttribute('data-label');
    this.activeOption = { field, direction, label };
    this.dispatchSortOptionChange();
  }

  dispatchSortOptionChange() {
    const resultsComponent = this.closest('searchspring-search-results, searchspring-collection');
    if (resultsComponent && typeof resultsComponent.onChangeSorting === 'function') {
      resultsComponent.onChangeSorting(this.activeOption);
    }
  }
}

if (!customElements.get('searchspring-sort-by')) {
  customElements.define('searchspring-sort-by', SearchspringSortBy);
}
